export const STUDIO_SPATIAL_UNITS = Object.freeze({
  PIXEL: "px",
  MILLIMETER: "mm",
  CENTIMETER: "cm",
  METER: "m",
});

export const STUDIO_SPATIAL_ORIENTATIONS = Object.freeze({
  PORTRAIT: "portrait",
  LANDSCAPE: "landscape",
  CUSTOM: "custom",
});

export const STUDIO_SPATIAL_LAYER_TYPES = Object.freeze({
  GRID: "grid",
  BACKGROUND: "background",
  CONTENT: "content",
  OVERLAY: "overlay",
  INTERACTION: "interaction",
});

const DEFAULT_WIDTH = 12000;
const DEFAULT_HEIGHT = 8000;
const DEFAULT_GRID_SIZE = 100;

function finiteNumber(value, fallback = 0) {
  const number = Number(value);

  return Number.isFinite(number)
    ? number
    : fallback;
}

function positiveNumber(value, fallback) {
  const number = finiteNumber(value, fallback);

  return number > 0
    ? number
    : fallback;
}

function normalizeUnit(unit) {
  const supportedUnits =
    Object.values(STUDIO_SPATIAL_UNITS);

  return supportedUnits.includes(unit)
    ? unit
    : STUDIO_SPATIAL_UNITS.MILLIMETER;
}

function normalizeOrientation(
  orientation,
  width,
  height
) {
  if (
    Object.values(STUDIO_SPATIAL_ORIENTATIONS)
      .includes(orientation)
  ) {
    return orientation;
  }

  if (width === height) {
    return STUDIO_SPATIAL_ORIENTATIONS.CUSTOM;
  }

  return width > height
    ? STUDIO_SPATIAL_ORIENTATIONS.LANDSCAPE
    : STUDIO_SPATIAL_ORIENTATIONS.PORTRAIT;
}

function normalizeOrigin(origin = {}) {
  return {
    x: finiteNumber(origin.x),
    y: finiteNumber(origin.y),
  };
}

function normalizeGrid(grid = {}) {
  return {
    visible: grid.visible !== false,
    enabled: grid.enabled !== false,
    snapEnabled: grid.snapEnabled !== false,
    size: positiveNumber(
      grid.size,
      DEFAULT_GRID_SIZE
    ),
    subdivisions: Math.max(
      1,
      Math.round(
        positiveNumber(grid.subdivisions, 1)
      )
    ),
  };
}

function normalizeLayer(layer, index) {
  if (!layer || typeof layer !== "object") {
    return null;
  }

  const supportedLayerTypes =
    Object.values(STUDIO_SPATIAL_LAYER_TYPES);

  const type =
    supportedLayerTypes.includes(layer.type)
      ? layer.type
      : STUDIO_SPATIAL_LAYER_TYPES.CONTENT;

  return {
    id:
      String(
        layer.id ||
        `studio-layer-${index + 1}`
      ),
    type,
    name:
      String(
        layer.name ||
        type
      ),
    visible: layer.visible !== false,
    locked: Boolean(layer.locked),
    opacity: Math.min(
      1,
      Math.max(
        0,
        finiteNumber(layer.opacity, 1)
      )
    ),
    order: finiteNumber(
      layer.order,
      index
    ),
    metadata:
      layer.metadata &&
      typeof layer.metadata === "object"
        ? { ...layer.metadata }
        : {},
  };
}

function normalizeLayers(layers = []) {
  if (!Array.isArray(layers)) {
    return [];
  }

  return layers
    .map(normalizeLayer)
    .filter(Boolean)
    .sort((first, second) =>
      first.order - second.order
    );
}

export function createStudioSpatialDocument({
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  unit = STUDIO_SPATIAL_UNITS.MILLIMETER,
  origin = {},
  orientation,
  grid = {},
  layers = [],
  metadata = {},
} = {}) {
  const normalizedWidth =
    positiveNumber(width, DEFAULT_WIDTH);

  const normalizedHeight =
    positiveNumber(height, DEFAULT_HEIGHT);

  return {
    schemaVersion: 1,
    width: normalizedWidth,
    height: normalizedHeight,
    unit: normalizeUnit(unit),
    origin: normalizeOrigin(origin),
    orientation: normalizeOrientation(
      orientation,
      normalizedWidth,
      normalizedHeight
    ),
    grid: normalizeGrid(grid),
    layers: normalizeLayers(layers),
    metadata:
      metadata &&
      typeof metadata === "object"
        ? { ...metadata }
        : {},
  };
}

export function validateStudioSpatialDocument(
  document
) {
  const errors = [];

  if (!document || typeof document !== "object") {
    return {
      valid: false,
      errors: [
        "Studio spatial document must be an object.",
      ],
    };
  }

  if (
    !Number.isFinite(Number(document.width)) ||
    Number(document.width) <= 0
  ) {
    errors.push(
      "Studio spatial document width must be positive."
    );
  }

  if (
    !Number.isFinite(Number(document.height)) ||
    Number(document.height) <= 0
  ) {
    errors.push(
      "Studio spatial document height must be positive."
    );
  }

  if (
    !Object.values(STUDIO_SPATIAL_UNITS)
      .includes(document.unit)
  ) {
    errors.push(
      "Studio spatial document unit is unsupported."
    );
  }

  if (
    !document.origin ||
    !Number.isFinite(Number(document.origin.x)) ||
    !Number.isFinite(Number(document.origin.y))
  ) {
    errors.push(
      "Studio spatial document origin is invalid."
    );
  }

  if (
    !document.grid ||
    !Number.isFinite(Number(document.grid.size)) ||
    Number(document.grid.size) <= 0
  ) {
    errors.push(
      "Studio spatial document grid size must be positive."
    );
  }

  if (!Array.isArray(document.layers)) {
    errors.push(
      "Studio spatial document layers must be an array."
    );
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

export function assertStudioSpatialDocument(
  document
) {
  const validation =
    validateStudioSpatialDocument(document);

  if (!validation.valid) {
    throw new TypeError(
      validation.errors.join(" ")
    );
  }

  return document;
}

export function getStudioSpatialBounds(
  document
) {
  assertStudioSpatialDocument(document);

  const left = Number(document.origin.x);
  const top = Number(document.origin.y);
  const width = Number(document.width);
  const height = Number(document.height);

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
    centerX: left + width / 2,
    centerY: top + height / 2,
  };
}

export function updateStudioSpatialDocument(
  document,
  patch = {}
) {
  assertStudioSpatialDocument(document);

  return createStudioSpatialDocument({
    ...document,
    ...patch,
    origin: {
      ...document.origin,
      ...(patch.origin || {}),
    },
    grid: {
      ...document.grid,
      ...(patch.grid || {}),
    },
    metadata: {
      ...document.metadata,
      ...(patch.metadata || {}),
    },
    layers:
      patch.layers === undefined
        ? document.layers
        : patch.layers,
  });
}
